<template>
  <b-collapse class="card" :open="isOpen" @open="onOpen">
    <template #trigger="props">
      <div class="card-header" role="button">
        <p class="card-header-title">
          <b-icon :icon="props.open ? 'menu-down' : 'menu-right'"></b-icon>
          Media Processing
          <span v-if="activeJobCount > 0" class="ml-2">
            <b-tag type="is-info">{{ activeJobCount }} active</b-tag>
          </span>
        </p>
      </div>
    </template>
    <div class="card-content">
      <!-- Source Type Toggle -->
      <div class="box source-type-box">
        <div class="columns is-vcentered">
          <div class="column is-narrow">
            <span class="has-text-weight-semibold">Source Type</span>
          </div>
          <div class="column is-narrow">
            <b-field>
              <b-radio-button v-model="selectedMode" native-value="single" type="is-primary">
                <b-icon icon="file-video" size="is-small"></b-icon>
                <span>Single TX File</span>
              </b-radio-button>
              <b-radio-button v-model="selectedMode" native-value="bulk" type="is-danger">
                <b-icon icon="file-multiple" size="is-small"></b-icon>
                <span>Separate Segment Files</span>
              </b-radio-button>
            </b-field>
          </div>
          <div class="column">
            <p class="is-size-7 has-text-grey">
              <template v-if="selectedMode === 'single'">
                <strong>Single TX File:</strong> One complete video file per episode (soft-segmented using timecodes).
                Workflows: QC, Transcode, TX Ingest, Playout Delivery, VOD Delivery
              </template>
              <template v-else>
                <strong>Separate Segment Files:</strong> Multiple source video files per episode that need stitching.
                Workflows: Link segments → Stitch → QC
              </template>
            </p>
          </div>
        </div>
      </div>

      <!-- ====================== -->
      <!-- BULK MODE - Use existing BulkStitchApproval component -->
      <!-- For content with separate source video files per segment (needs stitching) -->
      <!-- ====================== -->
      <template v-if="selectedMode === 'bulk'">
        <BulkStitchApproval
          :media-id="mediaId"
          :media-title="mediaTitle"
          :season-number="seasonNumber"
          @jobs-submitted="onJobsSubmitted"
        />
      </template>

      <!-- ====================== -->
      <!-- SINGLE FILE MODE - Use BulkEpisodeWorkflow component -->
      <!-- For TX-ready content (one video file per episode) -->
      <!-- Supports: QC, Transcode, TX Ingest, Playout Delivery, VOD Delivery -->
      <!-- ====================== -->
      <template v-else>
        <BulkEpisodeWorkflow
          :media-id="mediaId"
          :media-title="mediaTitle"
          :season-number="seasonNumber"
          @files-linked="onFilesLinked"
          @jobs-submitted="onJobsSubmitted"
          @playout-approved="onPlayoutApproved"
        />
      </template>
    </div>
  </b-collapse>
</template>

<script>
import BulkStitchApproval from '@/components/BulkStitchApproval.vue'
import BulkEpisodeWorkflow from '@/components/BulkEpisodeWorkflow.vue'
import mediaProcessingApi from '@/api/mediaProcessing'

export default {
  name: 'MediaProcessingPanel',

  components: {
    BulkStitchApproval,
    BulkEpisodeWorkflow
  },

  props: {
    mediaId: {
      type: [Number, String],
      default: null
    },
    /**
     * Mode determines which sub-component to show:
     * - 'bulk': BulkStitchApproval - For content with separate source video files per segment (needs stitching)
     * - 'single': BulkEpisodeWorkflow - For TX-ready content (one video file per episode)
     *             Supports: QC, Transcode, TX Ingest, Playout Delivery, VOD Delivery
     */
    mode: {
      type: String,
      default: 'bulk',
      validator: v => ['single', 'bulk'].includes(v)
    },
    mediaTitle: {
      type: String,
      default: ''
    },
    seasonNumber: {
      type: [Number, String],
      default: null
    },
    startOpen: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isOpen: this.startOpen,
      activeJobCount: 0,
      selectedMode: this.mode // Internal state, initialized from prop
    }
  },

  methods: {
    async onOpen () {
      await this.loadActiveJobCount()
    },

    async loadActiveJobCount () {
      try {
        const response = await mediaProcessingApi.listJobs({ limit: 20 })
        const jobs = response.jobs || []
        this.activeJobCount = jobs.filter(j =>
          ['queued', 'processing'].includes(j.status)
        ).length
      } catch (error) {
        console.error('Failed to load job count:', error)
      }
    },

    onFilesLinked () {
      this.$emit('files-linked')
    },

    onJobsSubmitted () {
      this.loadActiveJobCount()
      this.$emit('jobs-submitted')
    },

    onPlayoutApproved () {
      this.$emit('playout-approved')
    }
  }
}
</script>

<style scoped>
.card-header {
  cursor: pointer;
}

.card-header:hover {
  background-color: #f5f5f5;
}

.ml-2 {
  margin-left: 0.5rem;
}

.source-type-box {
  background-color: #f5f5f5;
  padding: 1rem;
  margin-bottom: 1rem;
}
</style>
